import { useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  Download,
  Upload,
  Trash2,
  FileText,
  MoreHorizontal,
  Ban,
  AlertCircle,
  RefreshCw,
} from 'lucide-react'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Skeleton } from '../components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu'
import {
  useIncomingInvoice,
  useAnnulIncomingInvoice,
  useUpdateIncomingInvoiceStatus,
  useUploadIncomingInvoiceFile,
  useDeleteIncomingInvoiceFile,
} from '../hooks/useIncomingInvoices'
import { IncomingInvoiceStatus } from '../types/incomingInvoice'
import { toast } from '../hooks/useToast'
import { api, getApiError } from '../lib/api'

const statusKeys: Record<IncomingInvoiceStatus, string> = {
  draft: 'incomingInvoices.status.draft',
  created: 'incomingInvoices.status.created',
  annulled: 'incomingInvoices.status.annulled',
  corrected: 'incomingInvoices.status.corrected',
  paid: 'incomingInvoices.status.paid',
  partially_paid: 'incomingInvoices.status.partiallyPaid',
  pending_processing: 'incomingInvoices.status.pendingProcessing',
}

const statusVariants: Record<IncomingInvoiceStatus, 'secondary' | 'warning' | 'success' | 'destructive' | 'default'> = {
  draft: 'secondary',
  created: 'warning',
  annulled: 'destructive',
  corrected: 'default',
  paid: 'success',
  partially_paid: 'warning',
  pending_processing: 'secondary',
}

export default function IncomingInvoiceDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const annulMutation = useAnnulIncomingInvoice()
  const updateStatusMutation = useUpdateIncomingInvoiceStatus()
  const uploadFileMutation = useUploadIncomingInvoiceFile()
  const deleteFileMutation = useDeleteIncomingInvoiceFile()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const invoiceId = Number(id)
  const { data: invoice, isLoading, error, refetch } = useIncomingInvoice(invoiceId)

  const formatCurrency = (amount: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency,
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const handleStatusChange = async (status: IncomingInvoiceStatus) => {
    if (!invoice) return
    try {
      await updateStatusMutation.mutateAsync({ id: invoice.id, status })
      toast({
        title: t('incomingInvoices.toast.statusUpdated'),
        description: t('incomingInvoices.toast.statusUpdatedDesc', { status: t(statusKeys[status]) }),
      })
    } catch (err) {
      const apiError = getApiError(err)
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: apiError.message,
      })
    }
  }

  const handleAnnul = async () => {
    if (!invoice) return
    if (!window.confirm(t('incomingInvoices.actions.confirmAnnul'))) return

    try {
      await annulMutation.mutateAsync(invoice.id)
      toast({
        title: t('incomingInvoices.toast.annulled'),
        description: t('incomingInvoices.toast.annulledDesc'),
      })
    } catch (err) {
      const apiError = getApiError(err)
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: apiError.message,
      })
    }
  }

  const handleDownloadFile = async (fileId: number, filename: string) => {
    try {
      const response = await api.get(`/incoming-invoices/${invoiceId}/files/${fileId}/download`, {
        responseType: 'blob',
      })
      const url = URL.createObjectURL(response.data)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      const apiError = getApiError(err)
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: apiError.message,
      })
    }
  }

  const handleUploadFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const maxSize = 20 * 1024 * 1024 // 20MB
    if (file.size > maxSize) {
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: t('incomingInvoices.files.tooLarge'),
      })
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    try {
      await uploadFileMutation.mutateAsync({ invoiceId, file })
      toast({
        title: t('incomingInvoices.files.uploaded'),
        description: t('incomingInvoices.files.uploadedDesc', { filename: file.name }),
      })
    } catch (err) {
      const apiError = getApiError(err)
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: apiError.message,
      })
    }

    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleDeleteFile = async (fileId: number, filename: string) => {
    if (!window.confirm(t('incomingInvoices.files.confirmDelete', { filename }))) return

    try {
      await deleteFileMutation.mutateAsync({ invoiceId, fileId })
      toast({
        title: t('incomingInvoices.files.deleted'),
        description: t('incomingInvoices.files.deletedDesc', { filename }),
      })
    } catch (err) {
      const apiError = getApiError(err)
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: apiError.message,
      })
    }
  }

  if (error) {
    const apiError = getApiError(error)
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <h3 className="text-lg font-semibold">{t('common.error')}</h3>
        <p className="text-muted-foreground mb-4">{apiError.message}</p>
        <Button onClick={() => refetch()}>
          <RefreshCw className="mr-2 h-4 w-4" />
          {t('common.retry')}
        </Button>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-6 w-20" />
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <Skeleton className="h-48" />
            <Skeleton className="h-64" />
          </div>
          <div className="space-y-6">
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
          </div>
        </div>
      </div>
    )
  }

  if (!invoice) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <FileText className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <h3 className="text-lg font-semibold">{t('incomingInvoices.detail.notFound')}</h3>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/dashboard/incoming-invoices')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('incomingInvoices.detail.backToList')}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/dashboard/incoming-invoices')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight">{invoice.invoiceNumber}</h1>
              <Badge variant={statusVariants[invoice.status] as any}>
                {t(statusKeys[invoice.status])}
              </Badge>
            </div>
            <p className="text-muted-foreground">{formatDate(invoice.date)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {/* Status actions */}
              {invoice.status === 'draft' && (
                <DropdownMenuItem onClick={() => handleStatusChange('created')}>
                  {t('incomingInvoices.actions.markAsCreated')}
                </DropdownMenuItem>
              )}
              {invoice.status === 'created' && (
                <>
                  <DropdownMenuItem onClick={() => handleStatusChange('paid')}>
                    {t('incomingInvoices.actions.markAsPaid')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleStatusChange('partially_paid')}>
                    {t('incomingInvoices.actions.markAsPartiallyPaid')}
                  </DropdownMenuItem>
                </>
              )}
              {invoice.status === 'pending_processing' && (
                <DropdownMenuItem onClick={() => handleStatusChange('created')}>
                  {t('incomingInvoices.actions.markAsCreated')}
                </DropdownMenuItem>
              )}
              {/* Annul for draft/created */}
              {(invoice.status === 'draft' || invoice.status === 'created') && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={handleAnnul}
                  >
                    <Ban className="mr-2 h-4 w-4" />
                    {t('incomingInvoices.actions.annul')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Info cards */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {/* Supplier info */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              {t('incomingInvoices.detail.supplierInfo')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <p className="font-semibold">{invoice.supplier.name}</p>
            {invoice.supplier.taxId && (
              <p className="text-sm text-muted-foreground">NIF: {invoice.supplier.taxId}</p>
            )}
          </CardContent>
        </Card>

        {/* Invoice info */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              {t('incomingInvoices.detail.invoiceInfo')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">{t('incomingInvoices.form.invoiceNumber')}</span>
                <span className="text-sm font-medium">{invoice.invoiceNumber}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">{t('incomingInvoices.form.date')}</span>
                <span className="text-sm font-medium">{formatDate(invoice.date)}</span>
              </div>
              {invoice.receivedDate && (
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">{t('incomingInvoices.form.receivedDate')}</span>
                  <span className="text-sm font-medium">{formatDate(invoice.receivedDate)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">{t('common.status')}</span>
                <Badge variant={statusVariants[invoice.status] as any}>
                  {t(statusKeys[invoice.status])}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">{t('incomingInvoices.detail.created')}</span>
                <span className="text-sm font-medium">{formatDate(invoice.createdAt)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">{t('incomingInvoices.detail.totalAmount')}</span>
                <span className="text-sm font-bold">{formatCurrency(invoice.totalAmount, invoice.currency)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">{t('incomingInvoices.form.currency')}</span>
                <span className="text-sm font-medium">{invoice.currency}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Notes */}
      {invoice.notes && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              {t('incomingInvoices.detail.notes')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{invoice.notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Files */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              {t('incomingInvoices.files.title')}
            </CardTitle>
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                className="hidden"
                onChange={handleUploadFile}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadFileMutation.isPending}
              >
                <Upload className="mr-2 h-4 w-4" />
                {uploadFileMutation.isPending
                  ? t('incomingInvoices.files.uploading')
                  : t('incomingInvoices.files.upload')}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {invoice.files.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center px-6">
              <FileText className="h-8 w-8 text-muted-foreground/50 mb-2" />
              <p className="text-sm text-muted-foreground">{t('incomingInvoices.files.noFiles')}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="px-4">{t('incomingInvoices.files.filename')}</TableHead>
                  <TableHead className="px-4">{t('incomingInvoices.files.size')}</TableHead>
                  <TableHead className="px-4">{t('incomingInvoices.files.uploadedAt')}</TableHead>
                  <TableHead className="px-4 w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoice.files.map(file => (
                  <TableRow key={file.id}>
                    <TableCell className="px-4">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="truncate max-w-[300px]">{file.originalFilename}</span>
                      </div>
                    </TableCell>
                    <TableCell className="px-4 text-muted-foreground">
                      {formatFileSize(file.fileSize)}
                    </TableCell>
                    <TableCell className="px-4 text-muted-foreground">
                      {formatDate(file.uploadedAt)}
                    </TableCell>
                    <TableCell className="px-4">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDownloadFile(file.id, file.originalFilename)}
                          title={t('incomingInvoices.files.download')}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDeleteFile(file.id, file.originalFilename)}
                          disabled={deleteFileMutation.isPending}
                          title={t('incomingInvoices.files.delete')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
